import {
  Box,
  Button,
  TextField,
  styled,
  keyframes,
  Typography,
  Paper,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import { Icon, divIcon, point } from "leaflet";
import { useEffect, useState, useRef } from "react";

// Keyframes for animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Professional styled components
const StyledMapContainer = styled(MapContainer)(({ theme }) => ({
  height: "500px",
  width: "100%",
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    height: "400px",
  },
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  marginBottom: theme.spacing(4),
  borderRadius: "20px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  animation: `${slideIn} 0.6s ease-out`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: "-50%",
    right: "-50%",
    width: "200%",
    height: "200%",
    background:
      "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const SearchForm = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(3),
  },
}));

const ProfessionalTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "12px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.6)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "white",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(0, 0, 0, 0.7)",
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(0, 0, 0, 0.8)",
  },
}));

const ProfessionalButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #ff6b6b 0%, #ee5a24 100%)",
  borderRadius: "12px",
  padding: "12px 32px",
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  boxShadow: "0 4px 15px rgba(255, 107, 107, 0.4)",
  "&:hover": {
    background: "linear-gradient(45deg, #ee5a24 0%, #ff6b6b 100%)",
    boxShadow: "0 6px 20px rgba(255, 107, 107, 0.6)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: "14px 32px",
  },
}));

const MapCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
  overflow: "hidden",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  animation: `${fadeIn} 0.8s ease-out`,
}));

const SuccessCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(4),
  borderRadius: "24px",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
  color: "white",
  boxShadow:
    "0 20px 60px rgba(102, 126, 234, 0.4), 0 8px 20px rgba(102, 126, 234, 0.2), inset 0 1px 0 rgba(255,255,255,0.2)",
  animation: `${fadeIn} 0.8s ease-out`,
  border: "1px solid rgba(255, 255, 255, 0.2)",
  position: "relative",
  overflow: "hidden",
  backdropFilter: "blur(10px)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: "-50%",
    right: "-20%",
    width: "150%",
    height: "150%",
    background:
      "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 60%)",
    pointerEvents: "none",
  },
}));

const SuccessContent = styled(CardContent)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(5),
  position: "relative",
  zIndex: 1,
  "&:last-child": {
    paddingBottom: theme.spacing(5),
  },
}));

const ErrorCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
  borderRadius: "16px",
  background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
  color: "white",
  boxShadow:
    "0 8px 32px rgba(255, 107, 107, 0.3), 0 4px 12px rgba(255, 107, 107, 0.2)",
  animation: `${fadeIn} 0.6s ease-out`,
  border: "none",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)",
    pointerEvents: "none",
  },
}));

const ErrorContent = styled(CardContent)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(4),
  position: "relative",
  zIndex: 1,
  "&:last-child": {
    paddingBottom: theme.spacing(4),
  },
}));

const Maps = () => {
  const [dataSource, setDataSource] = useState([]);
  const [searchCode, setSearchCode] = useState(""); // Input value
  const [selectedLocation, setSelectedLocation] = useState(null); // Stores matched location
  const [errorMessage, setErrorMessage] = useState(null); // Error message state
  const mapRef = useRef(null); // Ref for scrolling to the map

  useEffect(() => {
    getApiProducts(); // Initial fetch
    const interval = setInterval(() => {
      getApiProducts(); // Fetch data every minute
    }, 60000);

    return () => clearInterval(interval); // Clear the interval when the component is unmounted
  }, []);

  const getApiProducts = async () => {
    try {
      const productsRes = await fetch(`https://uktomars.co.uk/api/posts`);
      if (!productsRes.ok) {
        throw new Error(`HTTP error! Status: ${productsRes.status}`);
      }
      const productsData = await productsRes.json();
      setDataSource(
        productsData.map((item) => ({
          id: item.id,
          code: item.code,
          gps_id: item.gps_id,
          latitude: item.gps_device?.latitude,
          longitude: item.gps_device?.longitude,
          status: item.status,
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSearch = () => {
    setErrorMessage(null); // Clear any previous error
    const location = dataSource.find((item) => item.code === searchCode);
    if (location) {
      if (location.status === 0 && location.latitude && location.longitude) {
        setSelectedLocation({
          lat: location.latitude,
          lng: location.longitude,
          code: location.code,
          status: location.status,
        });
      } else if (location.status === 1) {
        setSelectedLocation({
          status: location.status,
          code: location.code,
        });
      } else {
        setSelectedLocation(null);
        setErrorMessage("Location not found or coordinates are missing!");
      }
    } else {
      setSelectedLocation(null);
      setErrorMessage(
        "Reference number not found! Please check and try again."
      );
    }
  };

  const customIcon = new Icon({
    iconUrl: require("./Image/marker-icon.png"),
    iconSize: [38, 38],
  });

  const createCustomClusterIcon = (cluster) => {
    return new divIcon({
      html: `<div style="
        height: 3.5rem; 
        width: 3.5rem; 
        border-radius: 50%; 
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        transform: translate(-25%, -25%); 
        display: flex; 
        justify-content: center; 
        align-items: center; 
        font-weight: 700; 
        font-size: 1.5rem;
        color: white;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        border: 3px solid white;
        font-family: 'Roboto', sans-serif;
      ">${cluster.getChildCount()}</div>`,
      className: "custom-marker-cluster",
      iconSize: point(35, 35, true),
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, mb: 6 }}>
      <SearchContainer elevation={0}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            textAlign: "center",
            fontWeight: 800,
            mb: 4,
            textShadow: "0 4px 8px rgba(0,0,0,0.2)",
            background: "linear-gradient(45deg, #ffffff 30%, #f0f8ff 90%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            position: "relative",
            zIndex: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "60px",
              height: "4px",
              background: "linear-gradient(90deg, #ff6b6b, #ee5a24)",
              borderRadius: "2px",
            },
          }}
        >
          Track Your Delivery
        </Typography>

        <SearchForm component="form" noValidate autoComplete="off">
          <ProfessionalTextField
            label="Enter Your Reference Number"
            variant="outlined"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-input": {
                color: "#000",
                fontWeight: 500,
              },
              "& .MuiOutlinedInput-input::placeholder": {
                color: "rgba(0, 0, 0, 0.6)",
                opacity: 1,
              },
            }}
            placeholder="e.g., REF123456"
          />
          <ProfessionalButton
            variant="contained"
            onClick={handleSearch}
            size="large"
          >
            Track Now
          </ProfessionalButton>
        </SearchForm>
      </SearchContainer>

      <div ref={mapRef}>
        {errorMessage && (
          <ErrorCard elevation={0}>
            <ErrorContent>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                ⚠️ {errorMessage}
              </Typography>
            </ErrorContent>
          </ErrorCard>
        )}

        {selectedLocation &&
          selectedLocation.status === 0 && ( // Conditionally render map if status is 0
            <MapCard elevation={0}>
              <StyledMapContainer
                center={[selectedLocation.lat, selectedLocation.lng]}
                zoom={15}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MarkerClusterGroup
                  chunkedLoading
                  iconCreateFunction={createCustomClusterIcon}
                >
                  <Marker
                    position={[selectedLocation.lat, selectedLocation.lng]}
                    icon={customIcon}
                  >
                    <Popup>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#333" }}
                      >
                        Reference: {selectedLocation.code}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                        Status: In Transit
                      </Typography>
                    </Popup>
                  </Marker>
                </MarkerClusterGroup>
              </StyledMapContainer>
            </MapCard>
          )}

        {selectedLocation &&
          selectedLocation.status === 1 && ( // Conditionally render text if status is 1
            <SuccessCard elevation={0}>
              <SuccessContent>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 900,
                    mb: 2,
                    textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                    letterSpacing: "-0.02em",
                    background:
                      "linear-gradient(45deg, #ffffff 0%, #f0f8ff 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  🎉 Delivery Successful! 🎉
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    opacity: 0.95,
                    mb: 4,
                    fontSize: "1.4rem",
                    textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  Your package has been delivered successfully
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "16px",
                    padding: "20px 32px",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    backdropFilter: "blur(15px)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                      pointerEvents: "none",
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      opacity: 1,
                      color: "white",
                      fontSize: "1.3rem",
                      textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    📦 Reference: {selectedLocation.code}
                  </Typography>
                </Box>
              </SuccessContent>
            </SuccessCard>
          )}
      </div>
    </Container>
  );
};

export default Maps;
